/**
 * Which read a caller accepts when one for the same key is already running.
 */
export enum ReadFreshness {
  /** The running read is good enough: the caller shares it (the default). */
  sharedWithRunning = 'sharedWithRunning',
  /**
   * The caller knows the data changed after the running read started (e.g. an event
   * said so): it starts its own read, which later callers share instead.
   */
  startedAfterChange = 'startedAfterChange',
}

/** Runs one read per key at a time and hands it to every caller that asks meanwhile. */
export interface SingleFlightGroup<TValue> {
  run(key: string, read: () => Promise<TValue>, freshness?: ReadFreshness): Promise<TValue>;
}

/**
 * Single flight (request coalescing): while a read of a key runs, other callers of
 * that key get the same promise instead of starting another read. A read started
 * after a change replaces the running one for later callers; the replaced one still
 * settles for those who already wait for it. A settled read, fulfilled or rejected,
 * is forgotten, so the next caller reads again: this is not a cache.
 */
export function createSingleFlightGroup<TValue>(): SingleFlightGroup<TValue> {
  const runningReads = new Map<string, Promise<TValue>>();

  function startRead(key: string, read: () => Promise<TValue>): Promise<TValue> {
    const runningRead = read().finally(() => {
      if (runningReads.get(key) === runningRead) {
        runningReads.delete(key);
      }
    });
    runningReads.set(key, runningRead);
    return runningRead;
  }

  return {
    run(key, read, freshness = ReadFreshness.sharedWithRunning) {
      const runningRead = runningReads.get(key);
      if (runningRead && freshness === ReadFreshness.sharedWithRunning) {
        return runningRead;
      }
      return startRead(key, read);
    },
  };
}
